import { styled } from '@porsche-code/kado-styled-system/jsx';
import { Spacer } from '@porsche-code/kado-ui';
import {
  PButton,
  PDivider,
  PHeading,
  PInputDate,
  PSelect,
  PSelectOption,
  PSwitch,
} from '@porsche-design-system/components-react';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ApplicationSelect } from '@/components';
import { NAMESPACES } from '@/config/i18n';
import {
  AnalyticsEvent,
  AnalyticsGranularity,
  useAnalyticsMarketsQuery,
} from '@/graphql';

import { Report, ReportFilter } from './-components';

export const Route = createFileRoute('/admin/analytics/_auth/')({
  component: Analytics,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="admin.analytics.headline" />,
    };
  },
});

function Analytics() {
  const { t } = useTranslation(NAMESPACES);
  const [filter, setFilter] = useState<ReportFilter>();
  const { data: marketOptions, isPending: marketsPending } =
    useAnalyticsMarketsQuery();
  const markets =
    marketOptions?.organizations.docs.toSorted((a, b) =>
      a.name.localeCompare(b.name),
    ) ?? [];

  const { control, handleSubmit } = useForm<ReportFilter>({
    defaultValues: {
      app: '',
      event: AnalyticsEvent.Login,
      dateFrom: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      dateTo: dayjs().format('YYYY-MM-DD'),
      granularity: AnalyticsGranularity.Day,
      unique: false,
      organizationId: undefined,
    },
  });

  const onSubmit: SubmitHandler<ReportFilter> = (data) => {
    setFilter({
      app: data.app.toLowerCase().replaceAll('_', '-'),
      event: data.event,
      dateFrom: data.dateFrom,
      dateTo: data.dateTo,
      granularity: data.granularity,
      unique: data.unique,
      organizationId: data.organizationId,
    });
  };

  return (
    <>
      <PHeading size="lg" aria-level={1} tag="h1">
        {t('admin.analytics.headline')}
      </PHeading>

      <Spacer mb="md" />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="app"
          rules={{ required: true }}
          render={({ field: { ref: _ref, onChange, ...field } }) => (
            <ApplicationSelect
              required
              includeDashboardOption
              label={t('admin.analytics.app')}
              onChange={(event) => {
                const app = (event.target as HTMLElement & { value: string })
                  .value;
                onChange(app ?? undefined);
              }}
              {...field}
            />
          )}
        />

        <Controller
          name="event"
          control={control}
          rules={{ required: true }}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <PSelect
              {...field}
              label={t('admin.analytics.event')}
              name="event"
              required
              value={value}
              onChange={(event) =>
                onChange(event.detail.value as AnalyticsEvent)
              }
            >
              {Object.values(AnalyticsEvent).map((event) => (
                <PSelectOption key={event} value={event}>
                  {t(`admin.analytics.events.${event}`)}
                </PSelectOption>
              ))}
            </PSelect>
          )}
        />

        <Controller
          name="organizationId"
          control={control}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <PSelect
              {...field}
              label={t('admin.analytics.market')}
              name="organizationId"
              value={value?.toString() ?? ''}
              disabled={marketsPending}
              filter
              onChange={(event) => {
                const organizationId = String(event.detail.value ?? '');
                onChange(organizationId ? Number(organizationId) : undefined);
              }}
            >
              <PSelectOption value="">
                {t('admin.analytics.allMarkets')}
              </PSelectOption>
              {markets.map((market) => (
                <PSelectOption key={market.id} value={market.id.toString()}>
                  {market.name}
                </PSelectOption>
              ))}
            </PSelect>
          )}
        />

        <Controller
          name="dateFrom"
          control={control}
          rules={{ required: true }}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <PInputDate
              {...field}
              label={t('admin.analytics.dateFrom')}
              required
              value={value}
              onInput={(event) =>
                onChange((event.target as HTMLInputElement).value)
              }
            />
          )}
        />

        <Controller
          name="dateTo"
          control={control}
          rules={{ required: true }}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <PInputDate
              {...field}
              label={t('admin.analytics.dateTo')}
              required
              value={value}
              onInput={(event) =>
                onChange((event.target as HTMLInputElement).value)
              }
            />
          )}
        />

        <Controller
          name="granularity"
          control={control}
          rules={{ required: true }}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <PSelect
              {...field}
              label={t('admin.analytics.granularity')}
              name="granularity"
              required
              value={value}
              onChange={(event) =>
                onChange(event.detail.value as AnalyticsGranularity)
              }
            >
              {Object.values(AnalyticsGranularity).map((granularity) => (
                <PSelectOption key={granularity} value={granularity}>
                  {t(`admin.analytics.granularities.${granularity}`)}
                </PSelectOption>
              ))}
            </PSelect>
          )}
        />

        <div>
          <label>{t('admin.analytics.unique')}</label>
          <Spacer mb="xs" />
          <Controller
            name="unique"
            control={control}
            render={({ field }) => (
              <PSwitch
                style={{ paddingBottom: '13px', paddingTop: '13px' }}
                hideLabel
                checked={field.value}
                onBlur={field.onBlur}
                ref={field.ref}
                onUpdate={(event) => field.onChange(event.detail.checked)}
              >
                {t('admin.analytics.unique')}
              </PSwitch>
            )}
          />
        </div>

        <PButton type="submit" variant="primary" icon="search">
          {t('admin.analytics.analyze')}
        </PButton>
      </Form>

      <Spacer mb="md" />
      <PDivider />

      <Spacer mb="md" />
      {filter && <Report filter={filter} />}
    </>
  );
}

const Form = styled('form', {
  base: {
    display: 'grid',
    gap: 'grid',
    gridAutoColumns: '1fr 1fr 1fr 200px 200px 1fr 1fr auto',
    gridAutoFlow: 'column',
    alignItems: 'end',
  },
});
